import React, { useState, useEffect } from "react";
import { Plus, AlertTriangle, X } from "lucide-react";
import { Modal } from "./Modal";
import { EquipmentTypeahead } from "./EquipmentTypeahead";
import { IssueNumberTypeahead } from "./IssueNumberTypeahead";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

const DISCIPLINES = ["Dimensional", "Pressure", "Torque", "Temperature", "Electronics", "Hydraulics", "Mass", "Flow"];

// All editable job-item statuses — same enum as backend core.py
const STATUS_OPTIONS = [
  "received", "assigned", "in_progress",
  "parts_required", "awaiting_spares", "awaiting_customer_approval",
  "awaiting_quote", "awaiting_subcontractor", "at_subcontractor",
  "calibrated", "adjusted", "repaired",
  "ready_for_admin", "ready_for_delivery",
];

const blank = () => ({
  description: "", manufacturer: "", part_number: "", serial_number: "",
  asset_number: "", issue_number: "", location: "Toolstore", cal_method: "traceable",
  group_item: false, group_number: "", discipline: "Dimensional", technician_id: "",
  calibration_period_days: 365, quantity: 1, receiving_condition: "In box/case/pouch",
  remarks: "", notes: "", priority: "normal", status: "received",
  date_received: new Date().toISOString().slice(0, 10),
  matched_equipment_id: null, matched_meta: null, unserviceable_flag: null,
});

/**
 * AddLineModal — adds a new equipment line to an existing job. Reuses the
 * receiving line UX but lets the user explicitly pick the starting status.
 */
export function AddLineModal({ job, onClose, onSaved }) {
  const [form, setForm] = useState(blank());
  const [techs, setTechs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [dupWarn, setDupWarn] = useState(null);  // { delivery_note_number, status } when typeahead resolves a busy item
  const [crossWarn, setCrossWarn] = useState(null);  // { customer_name, description } when SN belongs to a different customer

  useEffect(() => {
    api.get("/employees", { params: { role: "technician" } })
      .then((r) => setTechs(r.data)).catch(() => {});
  }, []);

  const set = (patch) => setForm((s) => ({ ...s, ...patch }));

  // SPT-PTE-2026-0045: identity fields must not be silently overwritten once
  // already filled in — the field actually searched always takes the exact
  // picked value; every other identity field only fills in if still blank.
  const IDENTITY_FIELDS = ["description", "manufacturer", "part_number", "serial_number", "asset_number", "issue_number"];

  const onPick = async (m, searchField) => {
    setForm((s) => {
      const next = { ...s };
      IDENTITY_FIELDS.forEach((k) => {
        if (m[k] === undefined || m[k] === null || m[k] === "") return;
        if (k === searchField || !s[k]) next[k] = m[k];
      });
      next.discipline = m.discipline || s.discipline;
      next.calibration_period_days = m.calibration_period_days || s.calibration_period_days;
      next.matched_equipment_id = m.id;
      return next;
    });
    toast.success(`Loaded existing record · ${m.calibration_history_count || 0} past calibration(s)`);
    setCrossWarn(null);
    // Check if this equipment is already in service
    try {
      const r = await api.get(`/equipment/${m.id}/active-job-check`);
      if (r.data?.is_in_active_job) {
        setDupWarn(r.data.active);
      } else {
        setDupWarn(null);
      }
    } catch { /* swallow */ }
  };

  const save = async () => {
    if (form.unserviceable_flag && !form.unserviceable_flag.acked) {
      toast.error("Confirm whether the unserviceable-flagged issue number can still be used");
      return;
    }
    if (!form.description.trim()) { toast.error("Description is required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        calibration_period_days: form.calibration_period_days
          ? parseInt(form.calibration_period_days) : null,
        quantity: parseInt(form.quantity) || 1,
        technician_id: form.technician_id || null,
        equipment_id: form.matched_equipment_id || null,
      };
      delete payload.matched_equipment_id;
      delete payload.matched_meta;
      delete payload.unserviceable_flag;
      const r = await api.post(`/jobs/${job.id}/items`, payload);
      if (r.data?.duplicate_warning) {
        toast.warning(`Heads-up: this equipment was already on DN ${r.data.duplicate_warning.delivery_note_number} (${r.data.duplicate_warning.status})`);
      } else {
        toast.success("Line added to job");
      }
      onSaved && onSaved(r.data);
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const transferInstead = async () => {
    if (!dupWarn?.job_item_id) return;
    setTransferring(true);
    try {
      await api.post(`/job-items/${dupWarn.job_item_id}/transfer`, { target_job_id: job.id });
      toast.success(`Transferred to DN ${job.delivery_note_number || ""} — no duplicate line created`);
      onSaved && onSaved({});
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setTransferring(false); }
  };

  return (
    <Modal open onClose={onClose} icon={Plus}
      title={`Add equipment line to ${job.delivery_note_number || ""}`}
      subtitle={`${job.customer_name || ""} · ${(job.items?.length || 0)} existing line(s)`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn gold" onClick={save} disabled={busy}
          data-testid="add-line-save">
          <Plus size={14} /> {busy ? "Saving…" : "Add line"}
        </button>
      </>}>

      {job.job_status === "closed" && (
        <div className="card pad" data-testid="add-line-closed-job-notice"
          style={{ marginBottom: 12, background: "var(--card-2,#f2f5fb)", border: "1px solid #cdd9ef",
                   display: "flex", gap: 10, alignItems: "flex-start" }}>
          <AlertTriangle size={16} style={{ color: "#3a5a9a", marginTop: 1 }} />
          <div style={{ fontSize: 12.5 }}>
            <div className="t-strong">This job is currently Closed</div>
            <div className="t-mut" style={{ marginTop: 2 }}>
              Adding this line will automatically reactivate the job — its status will move off
              &quot;Closed&quot; while this new line is worked on.
            </div>
          </div>
        </div>
      )}

      {dupWarn && (
        <div className="card pad" data-testid="add-line-dup-warning"
          style={{ marginBottom: 12, background: "#fff8e6", border: "1px solid #e3d28a",
                   display: "flex", gap: 10, alignItems: "flex-start" }}>
          <AlertTriangle size={16} style={{ color: "#8a5a00", marginTop: 1 }} />
          <div style={{ fontSize: 12.5, flex: 1 }}>
            <div className="t-strong">Already in service</div>
            <div className="t-mut" style={{ marginTop: 2 }}>
              This equipment is on DN <b className="mono">{dupWarn.delivery_note_number || "—"}</b>
              {dupWarn.technician_name && <> · with <b>{dupWarn.technician_name}</b></>}
              {dupWarn.status && <> · status <b>{dupWarn.status.replace(/_/g, " ")}</b></>}.
              Move it here instead of creating a second line, or add anyway if this is genuinely
              a separate physical item.
            </div>
            <button type="button" className="btn ghost sm" style={{ marginTop: 8 }}
              onClick={transferInstead} disabled={transferring}
              data-testid="add-line-transfer-btn">
              {transferring ? "Transferring…" : `Transfer to DN ${job.delivery_note_number || "this job"} instead`}
            </button>
          </div>
          <button className="icon-btn" style={{ width: 22, height: 22 }} onClick={() => setDupWarn(null)}>
            <X size={12} />
          </button>
        </div>
      )}

      {crossWarn && (
        <div className="card pad" data-testid="add-line-cross-customer-warning"
          style={{ marginBottom: 12, background: "var(--red-bg, #fde7e7)", border: "1px solid #f0b8b8",
                   display: "flex", gap: 10, alignItems: "flex-start" }}>
          <AlertTriangle size={16} style={{ color: "var(--red, #b3261e)", marginTop: 1 }} />
          <div style={{ fontSize: 12.5 }}>
            <div className="t-strong">Duplicate risk — serial number belongs to another customer</div>
            <div className="t-mut" style={{ marginTop: 2 }}>
              This serial number is already registered under <b>{crossWarn.customer_name || "another customer"}</b>
              {crossWarn.description && <> ({crossWarn.description})</>}. Double-check the serial number before
              proceeding — this may create a duplicate equipment record.
            </div>
          </div>
          <button className="icon-btn" style={{ width: 22, height: 22 }} onClick={() => setCrossWarn(null)}>
            <X size={12} />
          </button>
        </div>
      )}

      <div className="form-grid three">
        <div className="fld">
          <label>Serial number</label>
          <EquipmentTypeahead customerId={job.customer_id} field="serial_number"
            value={form.serial_number} placeholder="Type to search existing…"
            onChange={(v) => set({ serial_number: v, matched_equipment_id: null })}
            onPick={(m) => onPick(m, "serial_number")} onCreateNew={() => {}}
            onCrossCustomerMatch={(cm) => setCrossWarn(cm[0] || null)}
            testid="add-line-serial" />
        </div>
        <div className="fld">
          <label>Part number</label>
          <EquipmentTypeahead customerId={job.customer_id} field="part_number" minimal
            value={form.part_number} placeholder="Type to search existing…"
            onChange={(v) => set({ part_number: v, matched_equipment_id: null })}
            onPick={(m) => onPick(m, "part_number")} onCreateNew={() => {}}
            testid="add-line-part" />
        </div>
        <div className="fld">
          <label>Issue number</label>
          <IssueNumberTypeahead value={form.issue_number}
            onChange={(v) => set({ issue_number: v })}
            onPick={(m) => onPick(m, "issue_number")}
            onUnserviceableStatusChange={(v) => set({ unserviceable_flag: v })}
            placeholder="Unique ID when there is no serial number"
            testid="add-line-issue" />
        </div>
        <div className="fld">
          <label>Description <span className="req">*</span></label>
          <EquipmentTypeahead customerId={job.customer_id} field="description" minimal
            value={form.description} placeholder="Type equipment name…"
            onChange={(v) => set({ description: v, matched_equipment_id: null })}
            onPick={(m) => onPick(m, "description")}
            testid="add-line-desc" />
        </div>
        <div className="fld">
          <label>Manufacturer</label>
          <input className="input" value={form.manufacturer}
            onChange={(e) => set({ manufacturer: e.target.value })} />
        </div>
        <div className="fld">
          <label>Date received</label>
          <input className="input" type="date" value={(form.date_received || "").slice(0, 10)}
            onChange={(e) => set({ date_received: e.target.value })}
            data-testid="add-line-date-received" />
        </div>
        <div className="fld">
          <label>Discipline</label>
          <select className="input" value={form.discipline}
            onChange={(e) => set({ discipline: e.target.value })}
            data-testid="add-line-discipline">
            {DISCIPLINES.map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div className="fld">
          <label>SANAS / Traceable</label>
          <select className="input" value={form.cal_method}
            onChange={(e) => set({ cal_method: e.target.value })}>
            <option value="traceable">Traceable</option>
            <option value="sanas">SANAS Accredited</option>
          </select>
        </div>
        <div className="fld">
          <label>Owner of Tool</label>
          <input className="input" value={form.location}
            onChange={(e) => set({ location: e.target.value })} />
        </div>
        <div className="fld">
          <label>Technician</label>
          <select className="input" value={form.technician_id}
            onChange={(e) => set({ technician_id: e.target.value })}>
            <option value="">— unassigned —</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="fld">
          <label>Cal. period</label>
          <select className="input mono" value={form.calibration_period_days || ""}
            onChange={(e) => set({ calibration_period_days: e.target.value })}>
            <option value="">— select —</option>
            <option value="180">180 days (6 months)</option>
            <option value="365">365 days (1 year)</option>
            <option value="730">730 days (2 years)</option>
            <option value="1095">1095 days (3 years)</option>
          </select>
        </div>
        <div className="fld">
          <label>Priority</label>
          <select className="input" value={form.priority}
            onChange={(e) => set({ priority: e.target.value })}>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        <div className="fld">
          <label>Starting status</label>
          <select className="input" value={form.status}
            onChange={(e) => set({ status: e.target.value })}
            data-testid="add-line-status">
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div className="fld">
          <label>Receiving condition</label>
          <select className="input" value={form.receiving_condition}
            onChange={(e) => set({ receiving_condition: e.target.value })}>
            {["In box/case/pouch", "Arrived without box", "Visibly damaged",
              "Missing accessories", "Dirty"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="fld" style={{ gridColumn: "span 3" }}>
          <label>Notes</label>
          <textarea className="input" rows={2} value={form.notes}
            onChange={(e) => set({ notes: e.target.value })} />
        </div>
        <div className="fld" style={{ display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
            <input type="checkbox" checked={form.group_item}
              onChange={(e) => set({ group_item: e.target.checked, group_number: e.target.checked ? form.group_number : "" })}
              data-testid="add-line-group-toggle" /> Part of a group
          </label>
          {form.group_item && (
            <input className="input mono" style={{ width: 130 }} value={form.group_number}
              onChange={(e) => set({ group_number: e.target.value })}
              placeholder="Group number e.g. BK309"
              data-testid="add-line-group-number" />
          )}
        </div>
      </div>
    </Modal>
  );
}
